import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { snakeCase, toUpper } from 'lodash';
import { Session } from './AuthContext';
import { getPropertyTypeKey } from '../system/property-type';
import { Rule } from '../system/rule';
import { FieldDefinition } from '../system/field';
import { useFeatureFlags } from './FeatureFlagsContext';
import { useDebugger } from './DebuggerContext';
import { useTenant } from './TenantContext';
import { Lookup as CommonLookups } from 'src/system/lookup';

export interface ControlField {
  Key: string;
  SourceKey?: string;
  Control?: string;
  Label?: string;
  GroupingKey?: string;
  ForceRequired?: boolean;
  ForceNeverDisabled?: boolean;
  ForcedReadOnly?: boolean;
  FeatureFlag?: string;
  Layout: 'fluid' | 'fluid-single' | 'paired' | 'single';
  AllowSetFieldDefaults?: boolean;
  HideFromMainForm?: boolean;
  Traits?: Record<string, string>;
}

export interface Grouping {
  Key: string;
  Title: string;
}

export interface Section {
  name: string;
  fields: ControlField[];
}

export interface Group {
  name: string;
  sections: Section[];
}

export interface Modification {
  match: {
    field: string;
    value: string;
    set: number; // tracks sets of modifications from the same row in a csv (who therefore share values)
  };
  target: {
    field: string;
    value: string[];
  };
}

export interface Modifications {
  removeFromLookups: Modification[];
}

export interface Template {
  groups: Group[];
  groupings?: Grouping[];
  compareForListings: string[];
  compareForAgents: string[];
  modifications: Modifications;
}

export interface PropertyTemplates {
  RESIDENTIAL: Template;
  MULTI_FAMILY: Template;
  RESIDENTIAL_LEASE: Template;
  FARM: Template;
  COMMERCIAL_LEASE: Template;
  LAND: Template;
  COMMERCIAL_SALE: Template;
}

// Interfaces for the API response
export interface FieldMetadata {
  Key: string;
  LocaleKey: string;
  Name: string;
  Type: string;
  Collection?: boolean;
  MultiSelect?: boolean;
  Lookup?: string;
  Definition: FieldDefinition;
  ResourceType: string;
  HelpText?: string;
  dynamicLookup?: {
    lookupParent: string;
    context: {
      dictionary: string;
      standardName: string;
    };
  };
}

export interface Lookup {
  LookupKey?: string;
  LookupName: string;
  LookupValue: string;
  Description?: string | null;
  DisplayValue?: string;
}

export type Translation = { [key: string]: string };

export interface ListingTranslation {
  Property: Translation;
  OpenHouse: Translation;
  Media: Translation;
  Unit: Translation;
  Room: Translation;
  GreenVerification: Translation;
  Lookup: Translation;
}

export interface MetadataStatic {
  BadWords: string[];
  WarningWords: string[];
}

interface MetadataResponse {
  fields: FieldMetadata[];
  template: PropertyTemplates;
  lookup: { [key: string]: Lookup[] };
  relatedLookups: Record<string, Record<string, Record<string, Lookup[]>>>;
  translation: ListingTranslation;
  version: string;
  tenant: string;
  rules: Rule[];
  static: MetadataStatic;
  help?: Record<string, string>;
  /**
   * Map of MLS status code → required field system_names for that
   * transition (e.g. `SOLD: ['BuyerAgentMlsId', 'CloseDate', ...]`).
   * Populated by systems that expose a dedicated status-change flow;
   * systems without one omit it.
   */
  statusFieldsMap?: Record<string, string[]>;
}

interface MetadataContextType {
  field: (key: string) => FieldMetadata;
  getFieldByLocaleKey: (localeKey: string) => FieldMetadata;
  fields: () => FieldMetadata[];
  rules: () => Rule[];
  staticField: <T>(key: keyof MetadataStatic) => T;
  locale: () => any;
  currentMetadata: () => MetadataResponse | null;
  template: (propertyType: string) => Template;
  propertyTemplates: PropertyTemplates;
  flattenFields: (group: Group) => ControlField[];
  lookup: (key: string) => Lookup[] | undefined;
  lookups: () => string[];
  relatedLookups: (
    key: string,
    relatedField: string,
    field: string,
  ) => Lookup[] | undefined;
  version: string | null;
  loadMetadata: (session: Session) => Promise<void>;
  displayValueForLookup: (lookup: Lookup) => string;
  displayValueForListingField: (lookupKey: string, value: any) => string;
  lookupValueForListingField: (lookupKey: string, value: any) => string;
  getHelpText: (resourceType?: string, key?: string) => string | undefined;
  isLoading: boolean;
  error: Error | null;
}

export const MetadataContext = createContext<MetadataContextType | undefined>(
  undefined,
);

export const MetadataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { api } = useTenant();
  const { getFlags } = useFeatureFlags();
  const debug = useDebugger();
  const [metadata, setMetadata] = useState<MetadataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const versionCheckPerformed = useRef(false);
  const isLoadingRef = useRef(false);
  const templateCache = useRef<{ [key: string]: Template }>({});

  const loadMetadata = useCallback(async (session: Session) => {
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;

    debug.log('MetadataProvider', 'Loading metadata for application');
    debug.log('MetadataProvider', 'Checking local storage for metadata');

    try {
      const cachedMetadata = localStorage.getItem('metadata');
      const cachedTimestamp = localStorage.getItem('metadataTimestamp');

      if (cachedMetadata && cachedTimestamp) {
        const currentTime = new Date().getTime();
        const cacheAge = currentTime - parseInt(cachedTimestamp, 10);

        if (cacheAge < 7 * 24 * 60 * 60 * 1000) {
          const parsedMetadata = JSON.parse(cachedMetadata);
          setMetadata(parsedMetadata);
          setIsLoading(false);

          debug.log(
            'MetadataProvider',
            `Found metadata for application, checking latest version from server matches ${parsedMetadata.version}`,
          );

          if (!versionCheckPerformed.current) {
            await checkVersion(api, parsedMetadata.version, session);
            versionCheckPerformed.current = true;
          }

          isLoadingRef.current = false;
          return;
        }
      }

      debug.log('MetadataProvider', 'Nothing found, fetching latest metadata');
      await fetchMetadata(api, session);
    } catch (err) {
      debug.log('MetadataProvider', 'Unable to load metadata', 'error');
      console.error(err);

      setError(
        err instanceof Error ? err : new Error('Failed to load metadata'),
      );
      setIsLoading(false);
    }

    isLoadingRef.current = false;
  }, []);

  const removeUnusedMetadataFields = (data: MetadataResponse) => {
    Object.entries(data).forEach(([key, value]): any => {
      if (key === 'lookup') {
        const lookups = Object.values(value);
        lookups.forEach((lookup: any) => delete lookup.StatusDate);
      }
      if (key === 'rules') {
        const rules = value;
        rules.forEach((rule: any) => {
          delete rule.ModificationTimestamp;
          delete rule.OriginalEntryTimestamp;
          delete rule.RuleHelpText;
        });
      }
      // TEMP: Template DB has duplicate Openhouse translation, remove it
      if (key === 'translation') {
        delete (value as any).Openhouse;
      }
    });
  };

  const fetchMetadata = async (apiUrl: string, session: Session) => {
    debug.log('MetadataProvider', `Fetching latest metadata from server`);
    const response = await fetch(
      `${apiUrl}/get-fields?system=${session.user.tenant}`,
      {
        credentials: 'include',
      },
    );
    const data: MetadataResponse = await response.json();

    removeUnusedMetadataFields(data);

    setMetadata(data);
    localStorage.setItem('metadata', JSON.stringify(data));
    localStorage.setItem('metadataTimestamp', new Date().getTime().toString());
    setIsLoading(false);
    versionCheckPerformed.current = true;
  };

  const checkVersion = async (
    apiUrl: string,
    metadataVersion: string,
    session: Session,
  ) => {
    try {
      const response = await fetch(
        `${apiUrl}/get-fields-version?system=${session.user.tenant}`,
        {
          credentials: 'include',
        },
      );
      const version = await response.json();
      debug.log('MetadataProvider', `Server version is ${version}`);
      if (metadataVersion !== version) {
        await fetchMetadata(apiUrl, session);
      }
    } catch (err) {
      console.error('Failed to check metadata version:', err);
    }
  };

  const fields = () => {
    return metadata?.fields as FieldMetadata[];
  };

  const rules = () => {
    return metadata?.rules as Rule[];
  };

  const staticField = (key: keyof MetadataStatic): any => {
    return (metadata?.static as MetadataStatic)[key];
  };

  const locale = () => {
    return metadata?.translation as any;
  };

  const field = (key: string) => {
    return metadata?.fields.find((field) => field.Key === key) as FieldMetadata;
  };

  const getFieldByLocaleKey = (localeKey: string) => {
    return metadata?.fields.find(
      (field) => field.LocaleKey === localeKey,
    ) as FieldMetadata;
  };

  const relatedLookups = (key: string, relatedField: string, field: string) => {
    return metadata?.relatedLookups[key]?.[relatedField]?.[field] as
      | Lookup[]
      | undefined;
  };

  const template = useCallback(
    (propertyType: string): Template => {
      const flags = getFlags();

      // Create a cache key
      const cacheKey = propertyType;

      // Return cached result if available
      if (templateCache.current[cacheKey]) {
        return templateCache.current[cacheKey];
      }

      const lookups = lookup(CommonLookups.PROPERTY_TYPE) || [];
      const matchingLookup = lookups.find(
        (lookup) => lookup.LookupValue === propertyType,
      );
      const prop = matchingLookup
        ? toUpper(
            snakeCase(
              matchingLookup.DisplayValue || matchingLookup.LookupValue,
            ),
          )
        : toUpper(snakeCase(propertyType));

      // Get the raw template
      const rawTemplate = metadata?.template[prop as keyof PropertyTemplates]
        ? (metadata?.template[prop as keyof PropertyTemplates] as Template)
        : (metadata?.template[
            getPropertyTypeKey(propertyType as any) as keyof PropertyTemplates
          ] as Template);

      if (!rawTemplate || !flags) {
        return rawTemplate;
      }

      // Deep clone to avoid modifying original
      const template = JSON.parse(JSON.stringify(rawTemplate));

      // Filter out fields based on feature flags
      if (template) {
        // filter out based on feature flags
        template.groups = template.groups.map((group: Group) => {
          group.sections = group.sections.map((section: Section) => {
            section.fields = section.fields.filter((field) => {
              // Keep fields without feature flags
              if (!field.FeatureFlag) return true;

              // Check if the flag is enabled
              const flag = flags[field.FeatureFlag];

              return flag && flag.enabled;
            });
            return section;
          });
          return group;
        });
      }

      // Store in cache
      templateCache.current[cacheKey] = template;

      return template;
    },
    [metadata, getFlags],
  );

  const flattenFields = (group: Group) => {
    if (!group.sections.length) {
      return [];
    }
    return group.sections.reduce((fields: ControlField[], section: Section) => {
      return [...fields, ...section.fields];
    }, [] as ControlField[]);
  };

  const lookup = (key: string): Lookup[] | undefined => {
    key = metadata?.translation.Lookup[key] || key;
    return metadata?.lookup?.[key];
  };

  const currentMetadata = (): MetadataResponse | null => {
    return metadata;
  };

  const lookups = (): string[] => {
    return Object.keys(metadata?.lookup || {});
  };

  const displayValueForListingField = (
    lookupKey: string,
    value: any,
  ): string => {
    if (!lookupKey || !value) {
      return value;
    }
    const lookups = lookup(lookupKey);
    if (!lookups) {
      return value;
    }

    const matchingLookup = lookups.find(
      (lookup) => lookup.LookupValue === value,
    );
    if (!matchingLookup) {
      return value;
    }

    return matchingLookup.DisplayValue || value;
  };

  const displayValueForLookup = (lookup: Lookup): string => {
    return lookup.DisplayValue || lookup.LookupValue;
  };

  const lookupValueForListingField = (
    lookupKey: string,
    value: any,
  ): string => {
    if (!lookupKey || !value) {
      return value;
    }
    const lookups = lookup(lookupKey);
    if (!lookups) {
      return value;
    }

    // Try to find by LookupValue first (exact match)
    let matchingLookup = lookups.find((lookup) => lookup.LookupValue === value);

    // If not found, try to find by DisplayValue or LookupKey
    if (!matchingLookup) {
      matchingLookup = lookups.find(
        (lookup) =>
          lookup.DisplayValue === value ||
          lookup.LookupKey === value ||
          lookup.LookupValue?.toUpperCase() === value?.toUpperCase() ||
          lookup.DisplayValue?.toUpperCase() === value?.toUpperCase(),
      );
    }

    // Return the LookupValue if found, otherwise return the original value
    return matchingLookup?.LookupValue || value;
  };

  const getHelpText = (
    resourceType?: string,
    key?: string,
  ): string | undefined => {
    return metadata?.help?.[`${resourceType}.${key}`];
  };

  return (
    <MetadataContext.Provider
      value={{
        lookup,
        fields,
        field,
        getFieldByLocaleKey,
        rules,
        flattenFields,
        staticField,
        locale,
        lookups,
        template,
        displayValueForListingField,
        lookupValueForListingField,
        displayValueForLookup,
        propertyTemplates: (metadata?.template || {}) as PropertyTemplates,
        getHelpText,
        currentMetadata,
        version: metadata?.version || null,
        loadMetadata,
        relatedLookups,
        isLoading,
        error,
      }}
    >
      {children}
    </MetadataContext.Provider>
  );
};

export const useMetadata = () => {
  const context = useContext(MetadataContext);
  if (context === undefined) {
    throw new Error('useMetadata must be used within a MetadataProvider');
  }
  return context;
};

/**
 * Utility function to create a ResourceField with metadata from the metadata context
 * Returns null if metadata cannot be found for the field
 *
 * @param fieldKey - The field key to look up in metadata
 * @param getFieldMetadata - Function to get field metadata (from useMetadata().field)
 * @param overrides - Optional partial ResourceField to override default values
 * @returns ResourceField with metadata or null if metadata not found
 */
export const createFieldWithMetadata = <T = any,>(
  fieldKey: string,
  getFieldMetadata: (key: string) => FieldMetadata,
  overrides?: T,
):
  | (T & {
      Key: string;
      Label?: string;
      Layout?: string;
      Metadata?: {
        Key: string;
        Type: string;
        Lookup?: string;
        Collection?: boolean;
        Definition?: FieldDefinition;
      };
    })
  | null => {
  const metadata = getFieldMetadata(fieldKey);

  // If metadata is not found, don't add the field to the form
  if (!metadata) {
    return null;
  }

  return {
    Key: fieldKey,
    Label: metadata?.Name || (overrides as any)?.Label,
    Layout: (overrides as any)?.Layout || 'single',
    Metadata: {
      Key: metadata.Key,
      Type: metadata.Type,
      Lookup: metadata.Lookup,
      Collection: metadata.Collection,
      Definition: metadata.Definition,
    },
    ...overrides,
  } as any;
};
